import "nprogress/nprogress.css";
import { Suspense } from "react";
import HttpsRedirect from "react-https-redirect";
import "react-perfect-scrollbar/dist/css/styles.css";
import App from "src/App";
import LoadingScreen from "src/components/LoadingScreen";
import "./config";
import { localStorageChecker } from "./config/localStorageChecker";
import { LocalSettingsProvider } from "./contexts/LocalSettingsContext";
import { SessionSettingsProvider } from "./contexts/SessionSettingsContext";

import ReactDOM from "react-dom";
import "./components-ds/index.css";
import "./design-system/index.css";
import "./views-ds/index.css";
import "./views/index.css";

// ⚠️ CRITICAL: Handle stale chunks (Vite dynamic imports on deployment)
// When a new version is deployed, old chunks are deleted. This handler
// catches those failures and reloads the page to get the new version.
// A 5-second cooldown (stored in sessionStorage) prevents infinite reload loops
// in case the chunk is genuinely missing or the error persists after reload.
const CHUNK_RELOAD_COOLDOWN_MS = 5_000;
const CHUNK_RELOAD_TIMESTAMP_KEY = "chunk_reload_last_at";

window.addEventListener("error", (event) => {
    // Catches both:
    // - "Failed to fetch dynamically imported module"
    // - "Loading chunk X failed" (with parent import error)
    const isChunkError =
        event?.message?.includes("Failed to fetch") ||
        event?.message?.includes("Loading chunk") ||
        event?.message?.includes("Failed to import");

    if (!isChunkError) {
        return;
    }

    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY) ?? 0);
    const elapsed = Date.now() - lastReloadAt;

    if (elapsed < CHUNK_RELOAD_COOLDOWN_MS) {
        // biome-ignore lint/suspicious/noConsole: dev debug logging
        console.warn(
            `Chunk load failed but reload is on cooldown (${Math.round(
                (CHUNK_RELOAD_COOLDOWN_MS - elapsed) / 1000
            )}s remaining). Skipping reload to prevent infinite loop.`
        );
        return;
    }

    // biome-ignore lint/suspicious/noConsole: dev debug logging
    console.warn("Chunk load failed → detected stale deployment, reloading app");
    sessionStorage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, String(Date.now()));
    window.location.reload();
});

localStorageChecker().then(() => {
    // we need to use ReactDOM.render and not createRoot due to react-router-dom v5
    ReactDOM.render(
        <Suspense fallback={<LoadingScreen />}>
            <LocalSettingsProvider>
                <SessionSettingsProvider>
                    <HttpsRedirect>
                        <App />
                    </HttpsRedirect>
                </SessionSettingsProvider>
            </LocalSettingsProvider>
        </Suspense>,
        document.getElementById("root")
    );
});
